@import '../../../globals/main.scss';

// -----------------------------------------------------------------------------
// SCSS tokens
// Internal layout / implementation constants
// -----------------------------------------------------------------------------
$versa-card-radius: rem16(16px);
$versa-card-gap: rem16(8px);
$versa-card-content-gap: rem16(16px);
$versa-card-content-padding-bottom: rem16(16px);
$versa-card-text-max-width: rem16(700px);
$versa-card-cover-padding: rem16(24px);
$versa-card-horizontal-gap: rem16(32px);
$versa-card-horizontal-padding: rem16(24px);
$versa-card-horizontal-padding-lg: rem16(32px);
$versa-card-sticky-top: rem16(16px);

$versa-card-meta-margin-bottom: rem16(4px);
$versa-card-cta-gap: rem16(16px);
$versa-card-cta-min-height: rem16(48px);
$versa-card-rounded-image-max-width: rem16(120px);

$versa-card-cover-overlay-rgb: rgb(13, 35, 75);
$versa-card-cover-text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8),
    0 4px 12px rgba(0, 0, 0, 0.5);

$versa-card-cta-icon-offset: -0.3rem;
$versa-card-cta-transition: 0.3s ease-in-out;

// -----------------------------------------------------------------------------
// Root / design tokens
// CSS variables are kept for styling + contextual overrides
// -----------------------------------------------------------------------------
pn-versa-card {
    display: flex;
    width: 100%;
    height: 100%;

    --versa-card-bg: transparent;
    --versa-card-heading-color: inherit;
    --versa-card-text-color: inherit;
    --versa-card-blockquote-border-color: inherit;
    --versa-link-color: #{$blue700};

    --versa-card-padding: #{rem16(16px)};
    --versa-card-heading-size: #{rem16(24px)};
    --versa-card-preamble-size: #{rem16(18px)};
    --versa-card-label-size: #{rem16(12px)};

    --rte-text-color: var(--versa-card-text-color);
    --rte-heading-color: var(--versa-card-heading-color);
    --rte-blockquote-border-color: var(--versa-card-blockquote-border-color);
    --rte-image-radius: #{$versa-card-radius};

    .pn-versa-card-label {
        display: block;
        font-size: var(--versa-card-label-size);
        text-transform: uppercase;
        margin: 0 0 $versa-card-meta-margin-bottom 0;
    }

    .pn-versa-card-heading {
        font-size: var(--versa-card-heading-size);
        margin: 0 0 $versa-card-meta-margin-bottom 0;
        color: var(--versa-card-heading-color);
    }

    .pn-versa-card-preamble {
        font-size: var(--versa-card-preamble-size);
        margin: 0 0 $versa-card-meta-margin-bottom 0;
    }
}

// -----------------------------------------------------------------------------
// Base structure
// -----------------------------------------------------------------------------
pn-versa-card {
    .pn-versa-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        gap: $versa-card-gap;
        position: relative;

        .pn-versa-card-imageWrapper {

            [slot='image'],
            [slot='image'] picture,
            [slot='image'] picture img {
                display: block;
                width: 100%;
                height: 100%;
            }

            [slot='image'] picture img {
                border-radius: $versa-card-radius;
                object-fit: cover;
            }
        }

        .pn-versa-card-content {            
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: 100%;
            width:100%;
            max-width: 100%;
            gap: $versa-card-content-gap;
            padding-bottom: $versa-card-content-padding-bottom;

            --pn-link-current-color: var(--versa-link-color);
            --rte-link-color: var(--pn-link-current-color);

            .pn-versa-card-textContent {
                display: flex;
                flex-direction: column;
                color: var(--versa-card-text-color);
                max-width: $versa-card-text-max-width;
                width:100%;
                margin:0 auto;
            }

            .pn-versa-card-linkwrapper {
                display: flex;
                align-items: center;
                min-height: $versa-card-cta-min-height;
                width: 100%;

                [slot='cta'] {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: $versa-card-cta-gap;
                    width: 100%;

                    a {
                        margin-bottom: 0;
                    }

                    pn-button[appearance='light'] {
                        color: unset;
                    }

                    a:not(.pn-button) {
                        color: var(--pn-link-current-color);
                        text-decoration: none;
                        font-weight: 400;

                        &:hover {
                            text-decoration: underline;
                        }
                    }

                    a.versa-card-cta-link-animated pn-icon svg path {
                        fill: var(--pn-link-current-color);
                    }
                }
            }
        }
    }
}

// -----------------------------------------------------------------------------
// Modifiers (visual / state)
// -----------------------------------------------------------------------------
pn-versa-card {
    .pn-versa-card-hasBg {
        position: relative;
        padding: var(--versa-card-padding);
        border-radius: $versa-card-radius;
        background-color: transparent;

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            background-color: var(--versa-card-bg);
            border-radius: inherit;
            z-index: 0;
            pointer-events: none;
            opacity: 1;
        }

        >* {
            position: relative;
            z-index: 1;
        }

        .pn-versa-card-content {
            padding-bottom: 0;
        }

        &.pn-versa-card-transparentBackground::before {
            opacity: 0.9;
        }
    }

    .pn-versa-card-hasImageCover {
        padding: 0;

        .pn-versa-card-imageWrapper {
            height: 100%;
            margin-bottom: 0;

            [slot='image'] picture img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &.pn-versa-card-hasImageCoverText {
            --pn-versa-hover-overlay-alpha: var(--pn-versa-opacity, 0.85);

            .pn-versa-card-imageWrapper {
                position: relative;
                height: 100%;

                &::before,
                &::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: $versa-card-radius;
                    pointer-events: none;
                }

                &::before {
                    background: radial-gradient(circle,
                            rgba($versa-card-cover-overlay-rgb, 0.8) 7%,
                            rgba($versa-card-cover-overlay-rgb, 0) 100%);
                    z-index: 1;
                }

                &::after {
                    background-color: rgba($versa-card-cover-overlay-rgb, var(--pn-versa-hover-overlay-alpha));
                    opacity: 0;
                    transition: opacity 1s;
                    z-index: 2;
                }

                [slot='image'] picture img {
                    position: relative;
                    z-index: 0;
                }
            }

            .pn-versa-card-content {
                position: absolute;
                inset: 0;
                z-index: 3;

                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                padding: $versa-card-cover-padding;
                max-width: unset;

                color: #{$white};
                text-shadow: $versa-card-cover-text-shadow;

                --pn-link-current-color: #{$white};
                --rte-text-color: #{$white};
                --rte-heading-color: #{$white};
                --rte-blockquote-text-color: #{$white};
                --rte-blockquote-border-color: rgba(255, 255, 255, 0.8);
                --rte-quote-author-color: #{$white};
                --rte-quote-author-bg: rgba(255, 255, 255, 0.16);

                a {
                    color: var(--pn-link-current-color);
                    text-shadow: none;
                }

                pn-button[appearance='light'] a {
                    color: $blue700;
                }

                .pn-versa-card-linkwrapper {
                    [slot='cta'] {
                        a.versa-card-cta-link-animated pn-icon svg path {
                            fill: var(--pn-link-current-color);
                        }
                    }
                }
            }

            &:hover .pn-versa-card-imageWrapper::after {
                opacity: 1;
            }
        }
    }

    .pn-versa-card-hasRoundedImage {
        .pn-versa-card-imageWrapper {
            width: auto;
            height: auto;

            [slot='image'] picture img {
                aspect-ratio: 1 / 1;
                width: 100%;
                height: 100%;
                max-width: $versa-card-rounded-image-max-width;
                border-radius: 50%;
            }
        }
    }
}

// -----------------------------------------------------------------------------
// Layout + alignment
// -----------------------------------------------------------------------------
pn-versa-card {
    .pn-versa-card-horizontal {
        flex-direction: column;

        &.pn-versa-card-imageGrow {
            .pn-versa-card-imageWrapper {
                [slot='image'] picture img {
                    height: 100%;
                }
            }
        }
    }

    .pn-versa-card-center {
        .pn-versa-card-imageWrapper {
            align-self: center;
        }

        .pn-versa-card-content {
            align-items: center;
            text-align: center;

            .pn-versa-card-linkwrapper {
                [slot='cta'] {
                    justify-content: center;
                }
            }
        }
    }
    
    .pn-versa-card-alignMiddle { 
        
        justify-content: center;

        
        .pn-versa-card-content {           
            height: auto;
        }
    }
}

// -----------------------------------------------------------------------------
// Theme / colors
// -----------------------------------------------------------------------------
pn-versa-card {
    &[data-card-color='white'] {
        --versa-card-bg: #{$white};
    }

    &[data-card-color='blue25'] {
        --versa-card-bg: #{$blue25};
        --versa-card-blockquote-border-color: #{$blue400};
    }

    &[data-card-color='blue50'] {
        --versa-card-bg: #{$blue50};
        --versa-card-blockquote-border-color: #{$blue400};
    }

    &[data-card-color='blue400'] {
        --versa-card-bg: #{$blue400};
    }

    &[data-card-color='blue900'] {
        --versa-card-bg: #{$blue900};
        --versa-card-heading-color: #{$blue200};
        --versa-card-text-color: #{$white};
        --versa-card-blockquote-border-color: #{$white};
        --versa-link-color: #{$white};
    }

    &[data-card-color='coral25'] {
        --versa-card-bg: #{$coral25};
        --versa-card-blockquote-border-color: #{$coral400};
    }

    &[data-card-color='coral50'] {
        --versa-card-bg: #{$coral50};
        --versa-card-blockquote-border-color: #{$coral400};
    }

    &[data-card-color='green25'] {
        --versa-card-bg: #{$green25};
        --versa-card-blockquote-border-color: #{$green400};
    }

    &[data-card-color='green50'] {
        --versa-card-bg: #{$green50};
        --versa-card-blockquote-border-color: #{$green400};
    }
}

// -----------------------------------------------------------------------------
// Responsive
// -----------------------------------------------------------------------------
@media (min-width: 600px) {
    pn-versa-card {
        .pn-versa-card-horizontal {
            flex-direction: row;
            gap: $versa-card-horizontal-gap;

            .pn-versa-card-imageWrapper,
            .pn-versa-card-content {
                flex: 1;
            }

            .pn-versa-card-imageWrapper {
                height: auto;

                [slot='image'] picture img {
                    height: auto;
                    position: sticky;
                    top: $versa-card-sticky-top;
                }
            }

            .pn-versa-card-content {
                width: 50%;
                box-sizing: border-box;
                justify-content: flex-start;
            }

            &.pn-versa-card-hasBg {
                padding: $versa-card-horizontal-padding;
            }

            &.pn-versa-card-horizontal--imgRight {
                flex-direction: row-reverse;
            }

            &.pn-versa-card-imageGrow {
                .pn-versa-card-imageWrapper {
                    height: 100%;
                }
            }

            &.pn-versa-card-center {
                .pn-versa-card-imageWrapper {
                    align-self: auto;
                }

                .pn-versa-card-content {
                    align-items: stretch;
                    text-align: center;

                    .pn-versa-card-textContent,
                    .pn-versa-card-linkwrapper {
                        align-self: center;
                    }

                    .pn-versa-card-linkwrapper {
                        [slot='cta'] {
                            justify-content: center;
                        }
                    }
                }
            }

            &.pn-versa-card-alignMiddle {
                .pn-versa-card-content {                    
                    justify-content: center; //overrides mobile alignMiddle which sets justify-content to space-between
                }
            }

            &.pn-versa-card-hasRoundedImage {
                .pn-versa-card-imageWrapper {
                    width: 50%;

                    [slot='image'] picture img {
                        max-width: unset;
                    }
                }
            }
        }
    }
}

@media (min-width: 1440px) {
    pn-versa-card {
        .pn-versa-card-horizontal.pn-versa-card-hasBg {
            padding: $versa-card-horizontal-padding-lg;
        }
    }
}

// -----------------------------------------------------------------------------
// CTA animation
// -----------------------------------------------------------------------------
pn-versa-card {
    .versa-card-cta-link-animated {
        pn-icon {
            position: relative;
            left: $versa-card-cta-icon-offset;
            transition: $versa-card-cta-transition;
        }

        &:hover pn-icon {
            left: 0;
        }
    }
}

// -----------------------------------------------------------------------------
// RTE
// -----------------------------------------------------------------------------
pn-versa-card {
    .pn-versa-card-RTEtext {
        [slot='rte'] {
            @include shared-rte;

            .pn-quote-card__section {
                max-width: none !important;
                background-color: transparent;
            }

            .quoteblock-section .pn-quote-card__section .pn-quote-card__quote {
                border: none !important;
                padding-left: 0 !important;
            }
        }
    }
}

// -----------------------------------------------------------------------------
// CMS Width Overrides
// -----------------------------------------------------------------------------
.onethirdwidth {
    pn-versa-card {
        --rte-forms-spacing: #{rem16(12px)};
    }
}

@media (min-width: 992px) {
    .onequarterwidth {
        pn-versa-card {
            --versa-card-padding: #{rem16(8px)};
            --versa-card-heading-size: #{rem16(20px)};
            --versa-card-preamble-size: #{rem16(17px)};
            --versa-card-label-size: #{rem16(11px)};

            --rte-h2-size: #{rem16(20px)};
            --rte-h3-size: #{rem16(18px)};
            --rte-h4-size: #{rem16(16px)};
        }
    }

    .halfwidth,
    .fullwidth {
        pn-versa-card {
            --rte-blockquote-margin: #{rem16(24px)} 0;
        }
    }
}